// src/pages/Pedido/ResumoPedido.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import Header from '../../components/Form/Header';
import Footer from '../../components/Form/Footer';
import { FaRegCheckSquare, FaSpinner } from "react-icons/fa";
import './ResumoPedido.css';
import MainContent from '../../components/Form/MainContent';
import { formatCurrency } from '../../utils/numberUtils';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Importando o CSS do Toastify
import food from "../../images/food.ico";
import { useTheme } from '../../contexts/ThemeContext';
import { getPedidoNumero } from '../../utils/pedidoUtils';
import PixNoResumo from './PixNoResumo';
import ResumoItensPedido from './ResumoItensPedido';

const ResumoPedido = () => {
  const { pedidoId } = useParams();
  const { fetchData } = useApi();
  const { configModulo } = useTheme();
  const [pedido, setPedido] = useState(null);
  const [erroResumo, setErroResumo] = useState('');
  const [recarregar, setRecarregar] = useState(0);
  const previousStatusRef = useRef(null); // Usado para armazenar o status anterior

  useEffect(() => {
    const fetchPedido = async () => {
      try {
        const result = await fetchData(`/PedidoDelivery/Resumo/${pedidoId}`); // Passa o id do pedido como parâmetro
        setPedido(result);
        setErroResumo('');

        // Verifica se o status mudou
        if (previousStatusRef.current && previousStatusRef.current !== result.statusPedidoDescricao) {
          sendNotification(result); // Envia notificação quando o status muda
        }
        previousStatusRef.current = result.statusPedidoDescricao; // Atualiza o status anterior
      } catch (error) {
        console.error('Erro ao buscar o pedido:', error);
        setErroResumo('Não foi possível carregar o resumo do pedido.');
      }
    };

    // Chama fetchPedido imediatamente
    fetchPedido();

    // Define o intervalo para chamar fetchPedido a cada 30 segundos
    const intervalId = setInterval(fetchPedido, 30000);

    // Limpa o intervalo ao desmontar o componente
    return () => clearInterval(intervalId);
  }, [pedidoId, recarregar]); // Dependência em pedidoId para refazer a requisição quando ele mudar

  // Função para enviar notificação para o telefone usando o react-toastify
  const sendNotification = (pedidoAtual) => {
    toast.info(`Notificação: O status do pedido #${getPedidoNumero(pedidoAtual, configModulo?.UsaIdDia)} mudou para ${pedidoAtual.statusPedidoDescricao}`, {
      position: "top-right",  // Posição da notificação
      autoClose: 10000, // Duração da notificação
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      icon: food, // Ícone para a notificação
    });
  };

  const formatDate = (date) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(date).toLocaleDateString("pt-BR", options);
  };

  const formatHora = (date) => {
    const options = { hour: '2-digit', minute: '2-digit' };
    return new Date(date).toLocaleTimeString("pt-BR", options);
  };

  if (!pedido) {
    return erroResumo ? <div role="alert">{erroResumo} <button type="button" onClick={() => setRecarregar(valor => valor + 1)}>Tentar novamente</button></div> : <p>Carregando...</p>;
  }

  return (
    <>
      <Header titulo="Resumo do Pedido" routeGoBack="/pedidos" />
      <MainContent className="resumo-pedido-main">
        <div className="resumo-container">
          <div className="resumo-confirmacao">
            <FaRegCheckSquare className="resumo-confirmacao-icone" aria-hidden="true" />
            <div className="resumo-confirmacao-texto">
              <h3>Pedido {pedido.statusPedidoDescricao}</h3>
              {pedido.statusPedidoDescricao === 'Em Produção'
                ? (<p>Previsão de Conclusão: {formatHora(pedido.dataHoraPrevisao)}</p>)
                : (<p>Realizado em: {formatDate(pedido.dataHoraPedido)}</p>)}
              {pedido.statusPedidoDescricao === 'Em análise' && (
                <div className="status-message">
                  <FaSpinner className="loading-icon" /> {/* Ícone de loading */}
                  <p>Aguarde o estabelecimento confirmar o pedido</p>
                </div>
              )}
            </div>
          </div>

          <PixNoResumo pedido={pedido} onRetry={() => setRecarregar(valor => valor + 1)} />

          <section className="resumo-detalhes" aria-label="Detalhes do pedido">
            <ResumoItensPedido pedido={pedido} />

            {pedido.enderecoCliente && (
              <>
                <h4>Informações de Entrega</h4>
                <p>Endereço: {pedido.enderecoCliente}</p>
              </>
            )}

            <p>Cliente: {pedido.nomeCliente}</p>
            <p>Telefone: {pedido.telefoneCliente}</p>

            <h4>Forma de Pagamento</h4>
            <p>{pedido.formaPagamento}</p>

            <h4>Valores</h4>
            <p>Valor dos Itens: {formatCurrency(pedido.valorItens)}</p>
            {pedido.taxaEntrega > 0 && <p>Taxa de Entrega: {formatCurrency(pedido.taxaEntrega)}</p>}
            {pedido.valorDesconto > 0 && <p>Desconto: -{formatCurrency(pedido.valorDesconto)}</p>}
            <p><b>Total: {formatCurrency(pedido.valorTotal)}</b></p>
          </section>
        </div>
      </MainContent>
      <Footer />
      <ToastContainer /> {/* Componente que renderiza as notificações toast */}
    </>
  );
};

export default ResumoPedido;
